import { useDispatch, useSelector } from 'react-redux';
import './chartStyle.css';
import SliderWithInput from '../SliderWithInput';
import { FormControl, MenuItem, Select } from '@mui/material';
import { updateWordCloudOptions } from '../../../redux/ChartPoperties/ChartControlsActions';
import { ChartOptionsStateProps } from '../CommonInterfaceForChartOptions';

const WordCloudStyles = ({ iconColor }: { iconColor?: string }) => {
  const dispatch = useDispatch();

  const { chartControls, tabTileProps } = useSelector((state: ChartOptionsStateProps) => ({
    chartControls: state.chartControls,
    tabTileProps: state.tabTileProps,
  }));

  const propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;
  const wcOptions = chartControls.properties[propKey]?.wordCloudOptions || {};

  const handleUpdate = (option: string, value: any) => {
    dispatch(updateWordCloudOptions(propKey, option, value));
  };

  const labelStyle = { padding: '5px 6% 5px 4%', color: iconColor ? iconColor : '#404040' };
  const selectStyle = {
    fontSize: '12px',
    width: '96%',
    margin: '0 auto 0.5rem 0.3rem',
    backgroundColor: 'white',
    height: '1.5rem',
    color: '#404040',
    textAlign: 'start',
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#2bb9bb',
    },
  };

  const menuItemStyle = {
    '& .MuiMenuItem-root.Mui-selected': {
      backgroundColor: 'rgba(43, 185, 187, 0.1) !important',
    },
    '& .MuiMenuItem-root.Mui-selected:hover': {
      backgroundColor: 'rgba(43, 185, 187, 0.2) !important',
    },
  };

  return (
    <div className="optionsInfo">
      <div className="optionDescription" style={{ ...labelStyle, marginTop: '10px' }}>
        Cloud Shape
      </div>
      <FormControl fullWidth size="small" style={{ fontSize: '12px', borderRadius: '4px' }}>
        <Select
          value={wcOptions.shape || 'square'}
          variant="outlined"
          onChange={(e) => handleUpdate('shape', e.target.value)}
          sx={selectStyle}
          MenuProps={{ PaperProps: { sx: menuItemStyle } }}
        >
          <MenuItem value="square" sx={{ padding: '2px 10px', fontSize: '12px' }}>
            Square
          </MenuItem>
          <MenuItem value="circle" sx={{ padding: '2px 10px', fontSize: '12px' }}>
            Circle
          </MenuItem>
          <MenuItem value="diamond" sx={{ padding: '2px 10px', fontSize: '12px' }}>
            Diamond
          </MenuItem>
          <MenuItem value="cardioid" sx={{ padding: '2px 10px', fontSize: '12px' }}>
            Heart (Cardioid)
          </MenuItem>
          <MenuItem value="star" sx={{ padding: '2px 10px', fontSize: '12px' }}>
            Star
          </MenuItem>
          <MenuItem value="triangle" sx={{ padding: '2px 10px', fontSize: '12px' }}>
            Triangle
          </MenuItem>
        </Select>
      </FormControl>

      <div className="optionDescription" style={labelStyle}>
        Word Orientation
      </div>
      <FormControl fullWidth size="small" style={{ fontSize: '12px', borderRadius: '4px' }}>
        <Select
          value={wcOptions.rotationMode || 'horizontal'}
          variant="outlined"
          onChange={(e) => handleUpdate('rotationMode', e.target.value)}
          sx={selectStyle}
          MenuProps={{ PaperProps: { sx: menuItemStyle } }}
        >
          <MenuItem value="horizontal" sx={{ padding: '2px 10px', fontSize: '12px' }}>
            Horizontal
          </MenuItem>
          <MenuItem value="ortho" sx={{ padding: '2px 10px', fontSize: '12px' }}>
            Horizontal & Vertical
          </MenuItem>
          <MenuItem value="random" sx={{ padding: '2px 10px', fontSize: '12px' }}>
            Random Angles
          </MenuItem>
        </Select>
      </FormControl>

      <div className="optionDescription" style={labelStyle}>
        Grid Spacing
      </div>
      <SliderWithInput
        percent={false}
        sliderValue={wcOptions.gridSize ?? 8}
        sliderMinMax={{ min: 0, max: 30, step: 1 }}
        changeValue={(value: any) => handleUpdate('gridSize', value)}
      />

      <div className="optionDescription" style={labelStyle}>
        Min Font Size
      </div>
      <SliderWithInput
        percent={false}
        sliderValue={wcOptions.minFontSize ?? 12}
        sliderMinMax={{ min: 5, max: 50, step: 1 }}
        changeValue={(value: any) => handleUpdate('minFontSize', value)}
      />

      <div className="optionDescription" style={labelStyle}>
        Max Font Size
      </div>
      <SliderWithInput
        percent={false}
        sliderValue={wcOptions.maxFontSize ?? 40}
        sliderMinMax={{ min: 20, max: 100, step: 1 }}
        changeValue={(value: any) => handleUpdate('maxFontSize', value)}
      />
    </div>
  );
};

export default WordCloudStyles;
